/** @flow */

import React from "react";
import { connect } from "react-redux";
import { Switch, Route, Redirect } from "react-router-dom";
import { ConnectedRouter } from "connected-react-router";
import { isAuthChecked, isAuthenticated } from "@modules/account/selectors";
import { Loader } from "@components";
import type { State } from "@store/reducers";

import PrivateRoute from "./private-route";
import Pages from "./pages";
import * as Paths from "./constants";
import { isStaticPage } from "@selectors";

type OwnProps = {|
  history: Object,
|};

type PropsFromState = {|
  isLoading: boolean,
  isAuthenticated: boolean,
|};

type Props = OwnProps & PropsFromState;

const Router = ({ history, isLoading, isAuthenticated }: Props) => {
  return (isStaticPage() ? (
    false
  ) : (
    isLoading
  )) ? (
    <Loader />
  ) : (
    <ConnectedRouter history={history}>
      <Switch>
        {/* Public routes */}
        <Route path={Paths.NOT_FOUND_PATH} component={Pages.Error404} exact />
        <Route path={Paths.SERVICE_UNAVAILABLE_PATH} component={Pages.ServiceUnavailable} exact />
        <Route path={Paths.NO_ACCESS_PATH} component={Pages.NoAccessPage} exact />

        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.HOME_PATH}
          component={Pages.HomePage}
          exact
        />

        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.GOODSHAPE_PRODUCTS_URL}
          component={Pages.GoodShapeProductsPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.SERVICE_HUB_URL}
          component={Pages.ServiceHubPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.SERVICE_DESK_URL}
          component={Pages.ServiceDeskPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.APP_PROMOTION_PATH}
          component={Pages.AppPromotionPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.MY_ABSENCES_PATH}
          component={Pages.MyAbsencesPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.ADVICE_LIBRARY_PATH}
          component={Pages.AdviceLibraryPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.SERVICE_DIRECTORY_PATH}
          component={Pages.ServiceDirectoryPage}
          exact
        />

        {/* Client */}
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.ADMINISTRATION_CLIENT_PATH}
          component={Pages.ClientListPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.ADMINISTRATION_APP_SETTINGS_PATH}
          component={Pages.AppPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.ADMINISTRATION_SLA_PERFORMANCE_PATH}
          component={Pages.SlaPerformancePage}
          exact
        />
        {/* Data */}
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.ADMINISTRATION_DATA_PAGE_PATH}
          component={Pages.DataPage}
          exact
        />
        {/* Hierarchy */}
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.ADMINISTRATION_HIERARCHY_ADMIN_PATH}
          component={Pages.HierarchyAdminPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.ADMINISTRATION_HIERARCHY_CHANGES_PATH}
          component={Pages.HierarchyChanges}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.ADMINISTRATION_HIERARCHY_CHANGE_LOG_PATH}
          component={Pages.HierarchyChangeLog}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.ADMINISTRATION_SERVICE_SETTINGS}
          component={Pages.SettingsPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.ADMINISTRATION_SERVICE_SECURITY}
          component={Pages.SecurityPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.ADMINISTRATION_SERVICE_EVENT_DETAILS}
          component={Pages.ServiceAlertDetailsPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.ADMINISTRATION_CLIENT_FEATURES_PATH}
          component={Pages.FeaturesPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.ADMINISTRATION_SERVICE_RETURN_TO_WORK_DETAILS}
          component={Pages.RtwDetailsPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.ADMINISTRATION_SERVICE_ABSENCE_MONITORING_DETAILS}
          component={Pages.AbsenceMonitoringDetailsPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.ADMINISTRATION_SERVICE_REFERRAL_DETAILS}
          component={Pages.ReferralDetailsPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.ADMINISTRATION_SERVICE_STANDARD_QUESTION_DETAILS}
          component={Pages.StandardQuestionDetailsPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.ADMINISTRATION_SERVICE_DYNAMIC_QUESTION_DETAILS}
          component={Pages.DynamicQuestionDetailsPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.ADMINISTRATION_SERVICE_NOTIFICATIONS_PAGE}
          component={Pages.NotificationsPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.ADMINISTRATION_SERVICE_USERS}
          component={Pages.UsersPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.ADMINISTRATION_SERVICE_SECURITY}
          component={Pages.SecurityPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.ADMINISTRATION_CLIENT_AUDIT_LOGS_PATH}
          component={Pages.AuditLogsPage}
          exact
        />
        {/* Employee */}
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.ADMINISTRATION_EMPLOYEE_MY_DETAILS}
          component={Pages.MyDetailsPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_DASHBOARD_PATH}
          component={Pages.DashboardOverviewPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_TOOLS_ACTIVITYFEED_PATH}
          component={Pages.DashboardActivityFeedPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_TOOLS_CORONAVIRUS_PATH}
          component={Pages.DashboardCoronavirusPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_TOOLS_INDEX_PATH}
          component={Pages.DashboardIndexPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_EMPLOYEE_ABSENCE_NOTES_PATH}
          component={Pages.EmployeeNotesPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_EMPLOYEE_DOCUMENTS_PATH}
          component={Pages.EmployeeDocumentsPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_TOOLS_TASKS_PATH}
          component={Pages.ReportingTasksPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_TASKS_FULL_PATH}
          component={Pages.ReportingTasksPage}
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_MY_NOTIFICATIONS_PATH}
          component={Pages.ReportingMyNotificationsPage}
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_REPORTS_OVERVIEW_PATH}
          component={Pages.ReportingReportsOverviewPage}
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_REPORTS_ABSENCE_DETAILS_PATH}
          component={Pages.AbsenceDetailsOverviewPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_REPORTS_ABSENCE_MONITORING_PATH}
          component={Pages.ReportsAbsenceMonitoringDetailsOverviewPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_ABSENCE_MONITORING_DETAILS_REPORT}
          component={Pages.AbsenceMonitoringDetailsReport}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_REPORTS_RTW_DETAILS_PATH}
          component={Pages.ReportsRtwDetailsOverview}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_RTW_DETAILS_REPORT}
          component={Pages.ReportsRtwDetailsReport}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_REPORTS_ALERT_DETAILS_PATH}
          component={Pages.ReportsAlertsDetailsPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_ALERT_DETAILS_REPORT}
          component={Pages.AlertDetailsReport}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_REPORTS_ABSENCE_DETAILS_REPORT_PATH}
          component={Pages.AbsenceDetailsReportPage}
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_FOCUS_LISTS_EMPLOYEE_PATH}
          component={Pages.FocusListsEmployeeOverviewPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_FOCUS_LISTS_EMPLOYEE_REPORT}
          component={Pages.FocusListsEmployeeReport}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_FOCUS_LISTS_MANAGER_PATH}
          component={Pages.FocusListsManagerOverviewPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_FOCUS_LISTS_MANAGER_REPORT}
          component={Pages.ReportingFocusListsManagerReportPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_FOCUS_LISTS_DEPARTMENT_PATH}
          component={Pages.FocusListsDepartmentOverviewPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_FOCUS_LISTS_DEPARTMENT_REPORT}
          component={Pages.FocusListsDepartmentReport}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_TRENDS_ABSENCE_RATE_PATH}
          component={Pages.TrendAbsenceRatePage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_TRENDS_ABSENCE_RATE_REPORT}
          component={Pages.TrendsAbsenceRatesReport}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_TRENDS_COMPLIANCE_PATH}
          component={Pages.TrendsCompliancePage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_TRENDS_COMPLIANCE_REPORT}
          component={Pages.TrendsComplianceReportPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_TRENDS_ABSENCE_REASON_PATH}
          component={Pages.TrendsAbsenceReasonsPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_TRENDS_ABSENCE_REASON_REPORT_PATH}
          component={Pages.TrendAbsenceReasonReportPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_EMPLOYEES_SEARCH_PATH}
          component={Pages.EmployeeSearchPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_EMPLOYEE_MONITORING_PATH}
          component={Pages.EmployeeMonitoringPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_EMPLOYEE_ABSENCES_PATH}
          component={Pages.EmployeesAbsencesPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_EMPLOYEE_ABSENCE_DETAILS_PATH}
          component={Pages.AbsenceDetailsPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_EMPLOYEE_ABSENCE_DETAILS_REVISION_PATH}
          component={Pages.AbsenceDetailsPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_EMPLOYEE_STATISTICS_PATH}
          component={Pages.EmployeeStatisticsPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_EMPLOYEE_REFERRALS}
          component={Pages.ReportingEmployeeReferralsPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_MI_PRESENTATIONS_PATH}
          component={Pages.MiReports}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_EMPLOYEE_WELLBEING_REFERRALS}
          component={Pages.WellbeingReferralsPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_EMPLOYEE_DETAILS_PATH}
          component={Pages.EmployeeDetailsPage}
          exact
        />
        {/* Profile */}
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.PROFILE_MY_DETAILS}
          component={Pages.MyDetailsPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.PROFILE_MY_EMPLOYEES}
          component={Pages.MyEmployees}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_EMPLOYEES_AUDIT_EMPLOYEE_DETAILS}
          component={Pages.MyEmployeeDetails}
          exact
        />
        {/* Help */}
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.HELP_PATH}
          component={Pages.HelpPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.HELP_QUESTION_PATH}
          component={Pages.HelpPage}
          exact
        />
        {/* Mobile App */}
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.MOBILE_APP_PATH}
          component={Pages.MobileAppPage}
          exact
        />
        {/* Health Reports */}
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_HEALTH_OVERVIEW_PATH}
          component={Pages.HealthOverviewPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_WORKFORCE_HEALTH_PATH}
          component={Pages.WorkForceHealthPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_CARE_PLANS_PATH}
          component={Pages.CarePlansPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_WELLBEING_ADVICE_PATH}
          component={Pages.WellbeingAdvicePage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_NURSE_ASSESSMENTS_PATH}
          component={Pages.NurseAssessmentsPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_PROVIDERS_REPORT_PATH}
          component={Pages.ProvidersPage}
          exact
        />
        {/* Referrals */}
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_REFERRALS_OVERVIEW}
          component={Pages.ReferralsOverviewPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_NEW_REFERRALS}
          component={Pages.NewReferralsPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_ACTIVE_REFERRALS}
          component={Pages.ActiveReferralsPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_CLOSED_REFERRALS}
          component={Pages.ClosedReferralsPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_MANAGER_KPI}
          component={Pages.ManagerKPI}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTING_OUTCOMES_REPORT}
          component={Pages.OutcomesReportPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTS_KPI_COMPLIANCE_PATH}
          component={Pages.KpiCompliancePage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTS_APPEALS_REPORT_PATH}
          component={Pages.AppealsReport}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.REPORTS_APPEALS_PATH}
          component={Pages.AppealsPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.ADMINISTRATION_SERVICE_INTEGRATIONS}
          component={Pages.IntegrationsPage}
          exact
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path={Paths.ADMINISTRATION_SERVICE_INTEGRATION_APPLICATION_APIS}
          component={Pages.IntegrationApplicationApisPage}
          exact
        />
        {/* Final catch all 404 redirect */}
        <Redirect to={Paths.NOT_FOUND_PATH} />
      </Switch>
    </ConnectedRouter>
  );
};

const mapStateToProps = (state: State): PropsFromState => ({
  isLoading: !isAuthChecked(state),
  isAuthenticated: isAuthenticated(state),
});

export default connect(mapStateToProps)(Router);
